import { useCallback, useEffect, useRef, useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
// Patches ParagraphNode and HeadingNode prototypes to persist __style in JSON.
// Must be imported before the editor is created.
import './nodes/StyledParagraphNode';
// Patches TableCellNode to support per-cell border color (serialize + DOM).
import './nodes/StyledTableCellNode';
// Patches TableNode to support custom table-wide width (serialize + DOM).
import './nodes/StyledTableNode';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { ListItemNode, ListNode } from '@lexical/list';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { CodeNode, CodeHighlightNode } from '@lexical/code';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { $getRoot, $createParagraphNode, $createTextNode } from 'lexical';
import { PlaceholderNode } from './nodes/PlaceholderNode';
import { PageBreakNode } from './nodes/PageBreakNode';
import { LayoutContainerNode, LayoutItemNode } from './nodes/LayoutNode';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import PlaceholderPlugin from './plugins/PlaceholderPlugin';
import PageBreakPlugin from './plugins/PageBreakPlugin';
import TableCellResizerPlugin from './plugins/TableCellResizerPlugin';
import TableContextMenuPlugin from './plugins/TableContextMenuPlugin';
import LayoutPlugin from './plugins/LayoutPlugin';
import LayoutResizerPlugin from './plugins/LayoutResizerPlugin';
import LayoutContextMenuPlugin from './plugins/LayoutContextMenuPlugin';
import styles from './LexicalDocEditor.module.scss';

function WordCountPlugin({ onCount }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const text = $getRoot().getTextContent();
        const trimmed = text.trim();
        const words = trimmed === '' ? 0 : trimmed.split(/\s+/).length;
        const sentences = trimmed === '' ? 0 : text.split(/[.!?।]+/).filter(s => s.trim()).length;
        onCount({ words, sentences });
      });
    });
  }, [editor, onCount]);
  return null;
}

const editorTheme = {
  paragraph: 'editor-paragraph',
  heading: {
    h1: 'editor-h1', h2: 'editor-h2', h3: 'editor-h3',
    h4: 'editor-h4', h5: 'editor-h5', h6: 'editor-h6',
  },
  list: {
    ul: 'editor-ul', ol: 'editor-ol',
    listitem: 'editor-listitem',
    listitemChecked: 'editor-listitem-checked',
    listitemUnchecked: 'editor-listitem-unchecked',
    nested: { listitem: 'editor-nested-listitem' },
  },
  table: 'editor-table',
  tableCell: 'editor-table-cell',
  tableCellHeader: 'editor-table-cell-header',
  tableCellSelected: 'editor-table-cell-selected',
  tableCellPrimarySelected: 'editor-table-cell-primary-selected',
  tableSelected: 'editor-table-selected',
  tableSelection: 'editor-table-selection',
  text: {
    bold: 'editor-bold',
    italic: 'editor-italic',
    underline: 'editor-underline',
    strikethrough: 'editor-strikethrough',
    code: 'editor-inline-code',
    subscript: 'editor-subscript',
    superscript: 'editor-superscript',
    underlineStrikethrough: 'editor-underline-strikethrough',
  },
  quote: 'editor-quote',
  code: 'editor-code-block',
  link: 'editor-link',
  hr: 'editor-hr',
};

const EDITOR_NODES = [
  HeadingNode,
  QuoteNode,
  ListNode, ListItemNode,
  TableNode, TableCellNode, TableRowNode,
  LinkNode, AutoLinkNode,
  CodeNode, CodeHighlightNode,
  HorizontalRuleNode,
  PlaceholderNode,
  PageBreakNode,
  LayoutContainerNode,
  LayoutItemNode,
];

function EditorRefPlugin({ editorRef }) {
  const [editor] = useLexicalComposerContext();
  editorRef.current = editor;
  return null;
}

function ImagePlugin() {
  const [editor] = useLexicalComposerContext();
  const inputRef = useRef(null);

  const handleFile = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const src = ev.target.result;
      editor.update(() => {
        const root = $getRoot();
        // Insert image as a paragraph with a native img — simple inline approach
        const p = $createParagraphNode();
        const text = $createTextNode('');
        p.append(text);
        root.append(p);
        // Use DOM directly after update
        setTimeout(() => {
          const sel = window.getSelection();
          if (sel) {
            const img = document.createElement('img');
            img.src = src;
            img.style.maxWidth = '100%';
            const range = document.createRange();
            const editorEl = document.querySelector('.editor-content');
            if (editorEl) {
              range.selectNodeContents(editorEl);
              range.collapse(false);
              sel.removeAllRanges();
              sel.addRange(range);
              document.execCommand('insertHTML', false, `<img src="${src}" style="max-width:100%"/>`);
            }
          }
        }, 50);
      });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, [editor]);

  return (
    <input
      ref={inputRef}
      type="file"
      accept="image/*"
      style={{ display: 'none' }}
      onChange={handleFile}
      id="lexical-image-input"
    />
  );
}

const PAGE_SIZES = {
  A4:     { width: 794,  height: 1123 },
  A3:     { width: 1123, height: 1587 },
  A5:     { width: 559,  height: 794  },
  Letter: { width: 816,  height: 1056 },
  Legal:  { width: 816,  height: 1344 },
  Stamp:  { width: 794,  height: 1304 },
};

const DEFAULT_PAGE_SETTINGS = {
  size: 'A4', marginTop: 96, marginBottom: 96, marginLeft: 72, marginRight: 72,
};

export default function LexicalDocEditor({
  initialState,
  onChange,
  zoom = 1,
  showHighlight = true,
  initialPageSettings,
  onPageSettingsChange,
  showHeader = false,
  showFooter = false,
  onToggleHeader,
  onToggleFooter,
  headerContent = '',
  footerContent = '',
  onHeaderChange,
  onFooterChange,
}) {
  const editorRef = useRef(null);
  const imageInputRef = useRef(null);
  const [pageSettings, setPageSettings] = useState(initialPageSettings || DEFAULT_PAGE_SETTINGS);
  const [internalZoom, setInternalZoom] = useState(zoom);
  const [docStats, setDocStats] = useState({ words: 0, sentences: 0 });
  const handleDocCount = useCallback((counts) => setDocStats(counts), []);

  // Local state for header/footer — initialized from prop on mount (component remounts
  // via key={currentDocId} on doc switch, so the initial value always seeds correctly).
  // Keeping it local avoids the prop-roundtrip that caused content loss on toggle.
  const [localHeaderContent, setLocalHeaderContent] = useState(headerContent);
  const [localFooterContent, setLocalFooterContent] = useState(footerContent);

  // Sync when the parent hands in a fresh set of settings (doc switched, etc.)
  useEffect(() => {
    if (initialPageSettings) setPageSettings(initialPageSettings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPageSettings]);

  const handlePageSettingsChange = useCallback((next) => {
    setPageSettings(next);
    onPageSettingsChange?.(next);
  }, [onPageSettingsChange]);

  // Guard against stored states with an empty root — Lexical throws if root has no
  // children. Fall back to null so the editor starts with a default empty paragraph.
  const safeInitialState = (() => {
    if (!initialState) {
      console.log('[LexicalDocEditor] initialState is null/empty — opening blank editor');
      return null;
    }
    try {
      const parsed = typeof initialState === 'string' ? JSON.parse(initialState) : initialState;
      const children = parsed?.root?.children;
      if (!Array.isArray(children) || children.length === 0) {
        console.warn('[LexicalDocEditor] root.children is empty — opening blank editor. State:', initialState.slice(0, 200));
        return null;
      }
      console.log(`[LexicalDocEditor] Loading state with ${children.length} root node(s):`, children.map(n => n.type));
      return initialState;
    } catch (e) {
      console.error('[LexicalDocEditor] Failed to parse initialState JSON:', e, initialState?.slice(0, 200));
      return null;
    }
  })();

  const initialConfig = {
    namespace: 'DocEditor',
    theme: editorTheme,
    nodes: EDITOR_NODES,
    onError: (error) => {
      console.error('[LexicalDocEditor] Lexical node error — editor will be empty. This usually means the document contains an unregistered or changed node type. Error:', error);
    },
    editorState: safeInitialState,
  };

  const handleChange = useCallback((editorState) => {
    if (onChange) onChange(JSON.stringify(editorState.toJSON()));
  }, [onChange]);

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className={`${styles.editorWrapper}${showHighlight ? '' : ' ' + styles.noHighlight}`}>
        <ToolbarPlugin
          imageInputRef={imageInputRef}
          pageSettings={pageSettings}
          onPageSettingsChange={handlePageSettingsChange}
          showHeader={showHeader}
          showFooter={showFooter}
          onToggleHeader={onToggleHeader}
          onToggleFooter={onToggleFooter}
        />
        <div className={styles.editorScroll}>
          <div
            className={styles.editorPage}
            style={{
              width: pageSettings.size === 'Custom' ? (pageSettings.customWidth ?? 794) : (PAGE_SIZES[pageSettings.size]?.width ?? 794),
              minHeight: pageSettings.size === 'Custom' ? (pageSettings.customHeight ?? 1123) : (PAGE_SIZES[pageSettings.size]?.height ?? 1123),
              paddingTop: pageSettings.marginTop,
              paddingBottom: pageSettings.marginBottom,
              paddingLeft: pageSettings.marginLeft,
              paddingRight: pageSettings.marginRight,
              transform: internalZoom !== 1 ? `scale(${internalZoom})` : undefined,
              transformOrigin: 'top center',
              transition: 'transform 0.15s ease',
            }}
            onClick={() => editorRef.current?.focus()}
          >
            {showHeader && (
              <div className={styles.editorHeaderFooter} onClick={e => e.stopPropagation()}>
                <span className={styles.editorSectionLabel}>Header</span>
                <textarea
                  className={styles.editorHeaderFooterTextarea}
                  placeholder="Header text — appears on every page when printed..."
                  value={localHeaderContent}
                  onChange={e => { setLocalHeaderContent(e.target.value); onHeaderChange?.(e.target.value); }}
                />
              </div>
            )}

            <RichTextPlugin
              contentEditable={<ContentEditable className="editor-content" />}
              placeholder={
                <div className="editor-placeholder" style={{ fontSize: 11, lineHeight: 1.8, color: '#bbb', maxWidth: 520 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#adb5bd', marginBottom: 8 }}>
                    ✦ ডকুমেন্ট টেমপ্লেট এডিটর
                  </div>
                  <div style={{ marginBottom: 6 }}>
                    এখানে যেকোনো আইনি বা অফিসিয়াল ডকুমেন্টের টেমপ্লেট তৈরি করুন। টেক্সট, টেবিল, ছবি, পেজ ব্রেক — সব কিছু যোগ করা যাবে।
                  </div>
                  <div style={{ marginBottom: 4, fontWeight: 600, color: '#adb5bd' }}>{'{{প্লেসহোল্ডার}} সিস্টেম:'}</div>
                  <div style={{ marginBottom: 2 }}>{'• {{ }} চিহ্নের মধ্যে যেকোনো নাম লিখুন — যেমন {{নাম}}, {{তারিখ}}, {{ঠিকানা}}'}</div>
                  <div style={{ marginBottom: 2 }}>• প্লেসহোল্ডারগুলো স্বয়ংক্রিয়ভাবে ডানপাশের "Fields & Steps" প্যানেলে যুক্ত হবে</div>
                  <div style={{ marginBottom: 2 }}>• প্রতিটি ফিল্ডের ধরন (টেক্সট, তারিখ, ড্রপডাউন ইত্যাদি) ও লেবেল সেখান থেকে সেট করুন</div>
                  <div style={{ marginBottom: 8 }}>• ব্যবহারকারী ফর্ম পূরণ করলে প্লেসহোল্ডারের জায়গায় তার দেওয়া তথ্য বসে যাবে</div>
                  <div style={{ fontStyle: 'italic', color: '#d0d0d0' }}>
                    {'উদাহরণ: "আমি {{আবেদনকারীর_নাম}}, পিতা: {{পিতার_নাম}}, গ্রাম: {{গ্রাম}}, তারিখ: {{তারিখ}}"'}
                  </div>
                </div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />

            {showFooter && (
              <div className={styles.editorHeaderFooter} style={{ borderTop: '1.5px dashed #dee2e6', borderBottom: 'none', paddingTop: 10, paddingBottom: 6, marginTop: 4 }} onClick={e => e.stopPropagation()}>
                <span className={styles.editorSectionLabel}>Footer</span>
                <textarea
                  className={styles.editorHeaderFooterTextarea}
                  placeholder="Footer text — page number, date, signatures..."
                  value={localFooterContent}
                  onChange={e => { setLocalFooterContent(e.target.value); onFooterChange?.(e.target.value); }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Status bar — like Word's bottom bar */}
        <div className={styles.statusBar}>
          <div className={styles.statusLeft}>
            <span>{pageSettings.size}</span>
            <span className={styles.statusDot}>·</span>
            <span>T {(pageSettings.marginTop / 96).toFixed(2)}"</span>
            <span className={styles.statusDot}>·</span>
            <span>B {(pageSettings.marginBottom / 96).toFixed(2)}"</span>
            <span className={styles.statusDot}>·</span>
            <span>L {(pageSettings.marginLeft / 96).toFixed(2)}"</span>
            <span className={styles.statusDot}>·</span>
            <span>R {(pageSettings.marginRight / 96).toFixed(2)}"</span>
          </div>
          <div className={styles.statusCenter}>
            <span>{docStats.words} words</span>
            <span className={styles.statusDot}>·</span>
            <span>{docStats.sentences} sentences</span>
          </div>
          <div className={styles.statusRight}>
            <button
              className={styles.zoomBtn}
              onClick={() => setInternalZoom(z => Math.max(0.3, parseFloat((z - 0.1).toFixed(1))))}
            >−</button>
            <span className={styles.zoomLabel}>{Math.round(internalZoom * 100)}%</span>
            <button
              className={styles.zoomBtn}
              onClick={() => setInternalZoom(z => Math.min(2.0, parseFloat((z + 0.1).toFixed(1))))}
            >+</button>
          </div>
        </div>
      </div>

      <HistoryPlugin />
      <ListPlugin />
      <CheckListPlugin />
      <TablePlugin hasCellMerge hasCellBackgroundColor />
      <TableCellResizerPlugin />
      <TableContextMenuPlugin />
      <LayoutPlugin />
      <LayoutResizerPlugin />
      <LayoutContextMenuPlugin />
      <LinkPlugin />
      <HorizontalRulePlugin />
      <PlaceholderPlugin />
      <PageBreakPlugin />
      <OnChangePlugin onChange={handleChange} />
      <EditorRefPlugin editorRef={editorRef} />
      <ImagePlugin />
      <WordCountPlugin onCount={handleDocCount} />
    </LexicalComposer>
  );
}
